/**
 * Encrypts URL parameters (IDs, codes, query values) so they don't appear in
 * plaintext in URLs, browser history, or server logs. AES-GCM, matched to the
 * backend `UrlProtector`: token = `"enc." + base64url(nonce(12) || ciphertext || tag(16))`.
 *
 * Decryption is tolerant — anything that isn't an `enc.` token passes through
 * unchanged, so the app keeps working during rollout.
 */
const SECRET = 'thalios-url-param-secret-change-me';
const PREFIX = 'enc.';

let keyPromise: Promise<CryptoKey> | null = null;
function getKey(): Promise<CryptoKey> {
  if (!keyPromise) {
    keyPromise = crypto.subtle
      .digest('SHA-256', new TextEncoder().encode(SECRET))
      .then((hash) => crypto.subtle.importKey('raw', hash, 'AES-GCM', false, ['encrypt', 'decrypt']));
  }
  return keyPromise;
}

function b64urlEncode(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64urlDecode(s: string): Uint8Array {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/').padEnd(s.length + ((4 - (s.length % 4)) % 4), '=');
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Encrypt a URL parameter. Empty stays empty; result is `enc.<token>`. */
export async function protectParam(value: string): Promise<string> {
  if (!value) return value;
  const key = await getKey();
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce, tagLength: 128 }, key, new TextEncoder().encode(value))
  );
  const token = new Uint8Array(nonce.length + ct.length);
  token.set(nonce, 0);
  token.set(ct, nonce.length);
  return PREFIX + b64urlEncode(token);
}

/**
 * Decrypt a token from {@link protectParam}, or `null` when it is an `enc.` token this build
 * cannot read (wrong key, truncated/mistyped QR, no Web Crypto outside a secure context).
 * A value that was never a token passes through unchanged.
 *
 * **Prefer this over {@link unprotectParam} for anything you are about to STORE or send to the
 * API.** The tolerant version reports failure by handing back the token itself, which reads as a
 * perfectly good plaintext value — so an unreadable table QR was persisted as the customer's
 * table and every later call carried `enc.…` where a table code belonged: the order POST 400'd
 * on the code pattern, `auth/guest` 401'd because no table has that code, and requesting the
 * bill 400'd the same way. Nothing recovered on its own, because the bad value was in
 * localStorage.
 */
export async function tryUnprotectParam(token: string): Promise<string | null> {
  if (!token || !token.startsWith(PREFIX)) return token;
  try {
    const raw = b64urlDecode(token.slice(PREFIX.length));
    const nonce = raw.slice(0, 12);
    const ct = raw.slice(12);
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce, tagLength: 128 }, await getKey(), ct);
    return new TextDecoder().decode(pt);
  } catch {
    return null;
  }
}

/**
 * Decrypt a token from {@link protectParam}. Tolerant — non-tokens pass through, and a token
 * that cannot be read comes back as-is so plaintext params keep working during rollout.
 *
 * Use {@link tryUnprotectParam} when the result is going to be stored or sent onward.
 */
export async function unprotectParam(token: string): Promise<string> {
  return (await tryUnprotectParam(token)) ?? token;
}
